// src/components/Layout.tsx
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Outlet, useOutletContext, useLocation } from "react-router-dom";
import SideNav from "./SideNav";
import { Suspense, useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@apollo/client/react";
import { COMPANIES_QUERY } from "../graphql/queries";
import { isPartnerAdminRole } from "../utils/roles";
import { useRouteSuspenseFallback } from "./skeletons/routeSuspenseFallback";
import { getRoutePageTitle } from "../utils/routePageTitles";
// import { BubbleChat } from 'flowise-embed-react'

type LayoutOutletContext = {
  setHeaderTitle: (title: string) => void;
  onMobileClose?: () => void;
};

const Layout = () => {
  const suspenseFallback = useRouteSuspenseFallback();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  // On mobile, start with panels closed. On desktop, start with sidebar open
  const [sidenavOpen, setSidenavOpen] = useState<boolean>(true);
  const [headerTitle, setHeaderTitle] = useState<string>("My Application");
  const { user, setTenantList } = useAuth();

  useEffect(() => {
    const title = getRoutePageTitle(location.pathname);
    if (title) setHeaderTitle(title);
  }, [location.pathname]);
  // const userJwtToken = localStorage.getItem("accessToken");
  // const bubbleTheme = {
  //   button: {
  //     backgroundColor: '#2563eb',
  //     right: 16,
  //     bottom: 12,
  //     size: 34,
  //     iconColor: '#ffffff',
  //     dragAndDrop: true,
  //     autoWindowOpen: { autoOpen: false }
  //   },

  //   tooltip: {
  //     showTooltip: false,
  //     tooltipMessage: 'Need help? Chat with us!',
  //     tooltipBackgroundColor: '#1f2937',
  //     tooltipTextColor: '#ffffff',
  //     tooltipFontSize: 14,
  //   },

  //   disclaimer: {
  //     title: 'Welcome to Critical Asset Assistant',
  //     message: 'Your intelligent companion for asset management.',
  //     textColor: '#f9fafb',
  //     buttonColor: '#1d4ed8',
  //     buttonText: 'Get Started',
  //     buttonTextColor: '#ffffff',
  //     blurredBackgroundColor: 'rgba(15,23,42,0.85)',
  //     backgroundColor: '#1e293b',
  //   },
  //   chatWindow: {
  //     showTitle: true,
  //     title: 'Critical Asset Assistant',

  //     welcomeMessage: 'Hello! How can I help you today?',
  //     backgroundColor: '#ffffff',

  //     starterPrompts: [
  //       'Show me my assets',
  //       'What are my locations?',
  //       'List asset categories',
  //       'Get asset summary'
  //     ],
  //     clearChatOnReload: false,
  //     botMessage: {
  //       backgroundColor: '#f1f5f9',
  //       textColor: '#0f172a',
  //       showAvatar: true,
  //       avatarSrc: '/images/bot.svg',
  //     },

  //     userMessage: {
  //       backgroundColor: '#1d4ed8',
  //       textColor: '#ffffff',
  //       showAvatar: true,
  //       avatarSrc: '/images/user.svg',
  //     },

  //     textInput: {
  //       placeholder: 'Type your message here...',
  //       backgroundColor: '#ffffff',
  //       textColor: '#0f172a',
  //       sendButtonColor: '#1d4ed8',
  //       autoFocus: true,
  //       maxChars: 500,
  //     },

  //     footer: {
  //       textColor: 'transparent',
  //       text: '',
  //       company: '',
  //     },
  //   },
  // };
  
  // Update state when breakpoint changes
  useEffect(() => {
    if (isMobile) {
      // On mobile, keep sidebar visible but panels closed
      setSidenavOpen(false);
    } else {
      setSidenavOpen(true);
    }
  }, [isMobile]);

  // Fetch companies using GraphQL
  const { data, error, refetch } = useQuery(COMPANIES_QUERY, {
    fetchPolicy: 'cache-and-network',
    context: { skipLoader: true },
  });

  const isPartnerAdmin = isPartnerAdminRole(user?.role);
  
  const partnerCompany = useMemo(() => {
    if (!isPartnerAdmin || !data || typeof data !== 'object' || !('companies' in data) || !Array.isArray((data as any).companies)) {
      return null;
    }
    return (data as { companies: any[] }).companies.find((c: any) => c.role?.toLowerCase() === "partner");
  }, [isPartnerAdmin, data]);
  
  // Memoize the tenants to prevent unnecessary re-renders
  const tenants = useMemo(() => {
    if (data && typeof data === 'object' && 'companies' in data && Array.isArray(data.companies)) {
      // Map companies to Tenant format
      return (data as { companies: any[] }).companies.map((company: any) => ({
        id: company.id,
        displayname: company.name || company.displayName || '',
        slug: company.id, // Using id as slug, adjust if you have a slug field
        ...company, // Include all company data
      }));
    }
    return [];
  }, [data]);

  // Track previous tenants to avoid unnecessary updates
  const prevTenantsRef = useRef<string | null>(null);
  
  useEffect(() => {
    // Create a stable string representation to compare
    const tenantsKey = JSON.stringify(tenants.map(t => t.id).sort());
    
    // Only update if the tenant IDs have actually changed
    if (prevTenantsRef.current !== tenantsKey) {
      prevTenantsRef.current = tenantsKey;
      setTenantList(tenants);
    }
  }, [tenants, setTenantList]);

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch company list:", error);
    }
  }, [error]);

  const handleMobileClose = () => {
    if (isMobile) {
      setSidenavOpen(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100vh", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", height: "100vh" }}>
        <SideNav 
          isOpen={sidenavOpen} 
          setIsOpen={setSidenavOpen}
          onMobileClose={handleMobileClose}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0, // Allows flexbox to shrink below content size
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            component="header"
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#fff",
              gap: 2,
              boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1)",
              p: 2,
              position: "sticky",
              top: 0,
              zIndex: 999,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 700 }}>
              {headerTitle}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {isPartnerAdmin && partnerCompany && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  padding: "6px 16px",
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  border: "1px solid #bfdbfe",
                  boxShadow: "0 2px 4px rgba(59, 130, 246, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 6px rgba(59, 130, 246, 0.1)",
                    background: "linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)",
                  }
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: "#3b82f6",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)",
                  }}
                >
                  {partnerCompany.name ? partnerCompany.name.charAt(0).toUpperCase() : 'P'}
                </Box>
                
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#1e3a8a",
                      letterSpacing: "0.5px",
                      lineHeight: 1.2
                    }}
                  >
                    {partnerCompany.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: "#3b82f6",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      lineHeight: 1.2
                    }}
                  >
                    Partner Portal
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            className="p-3"
            sx={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <Suspense fallback={suspenseFallback}>
              <Outlet context={{ setHeaderTitle, onMobileClose: handleMobileClose }} />
            </Suspense>
          </Box>
        </Box>
      </Box>
      {/* <div className="asset-chat-ui">
      <BubbleChat
        chatflowid="599a35f8-ed63-434b-ab6a-3544b263df23"
        apiHost="https://chatbot.criticalasset.com"
        theme={bubbleTheme}
        chatflowConfig={{ vars: { jwt: userJwtToken } }}
      />
    </div> */}
    </Box>
  );
};

export default Layout;

export const useLayoutContext = () => useOutletContext<LayoutOutletContext>();
